import type { DropdownItemType } from '@allesauto/core/dropdown';
import MakeModelVariant from '@allesauto/core/makeModelVariant';
import TextLink from '@allesauto/core/textLink';
import { GTMFilterCategory } from '@allesauto/utils/consts';
import { Add16, Close16 } from '@carbon/icons-react';
import clsx from 'clsx';
import isArray from 'lodash/isArray';
import { useRouter } from 'next/router';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getParsedUrlQuery } from '@/features/PCP/utils';
import { useGTM } from '@/global/hooks/useGTM';
import { setPageNumber } from '@/store/redux/actions/pages/pcp';
import {
  addFilter,
  fetchModelsByMake,
  removeFilter,
  selectPCPFilterMakeModelModels,
  selectPCPFilterMakeModelSelectedMakes,
  setMake,
  setModel,
} from '@/store/redux/slices/pages/pcp/filters/makeModel';

export interface MakeModelFilterProps {
  makeItems: DropdownItemType[];
  modelItems?: DropdownItemType[];
  addBtnText: string;
  removeBtnText: string;
  modelLabel: string;
  makeLabel: string;
  maxFilterLength: number;
}

const MakeModelFilter: FC<React.PropsWithChildren<MakeModelFilterProps>> = ({
  makeItems,
  addBtnText,
  removeBtnText,
  modelLabel,
  makeLabel,
  maxFilterLength,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const GTM = useGTM();
  const filterData = useSelector(
    (state: any) => state.pages.pcp.filters.makeModel.data
  );
  const [selectedModelGroup, setSelectedModelGroup] = useState<
    string | undefined
  >('');

  const makeModelFilterCount = useSelector(
    (state: any) => state.pages.pcp.filters.makeModel.data.length
  );

  const makeModels = useSelector(selectPCPFilterMakeModelModels);

  // fetch models for selected makes
  useEffect(
    () => {
      if (!router.isReady) {
        return;
      }
      const parsedUrlQuery = getParsedUrlQuery(router.query);

      const { makeModel } = parsedUrlQuery;

      if (makeModel && makeModel?.length > 0 && 'modelGroup' in makeModel[0]) {
        // value of "modelGroup" property in url
        const modelGroup = makeModel[0].modelGroup;
        setSelectedModelGroup(modelGroup);
      } else {
        setSelectedModelGroup('');
      }

      if (
        isArray(parsedUrlQuery.makeModel) &&
        parsedUrlQuery.makeModel?.length
      ) {
        parsedUrlQuery.makeModel.forEach(({ makes, models }, filterIndex) => {
          dispatch(
            setMake({
              makeName: makes as string,
              filterIndex,
            })
          );
          dispatch(
            setModel({
              modelName: selectedModelGroup
                ? selectedModelGroup
                : (models as string),
              filterIndex,
            })
          );
          dispatch(
            fetchModelsByMake({
              makeName: makes as string,
              filterIndex,
            })
          );
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.isReady, router.query]
  );

  const onMakeChange = (
    make: string | number | undefined,
    filterIndex: number
  ) => {
    if (make === null) {
      return onDeleteFilter(filterIndex);
    }

    dispatch(
      setMake({
        makeName: make as string,
        filterIndex,
      })
    );

    dispatch(
      setModel({
        modelName: '',
        filterIndex,
      })
    );

    make &&
      dispatch(
        fetchModelsByMake({
          makeName: make as string,
          filterIndex,
        })
      );

    dispatch(setPageNumber(1));

    GTM.setFilterInformation(GTMFilterCategory.MAKE_MODEL, {
      make: make as string,
    });
  };

  const onModelChange = (
    model: string | number | undefined,
    filterIndex: number
  ) => {
    dispatch(
      setModel({
        modelName: selectedModelGroup ? selectedModelGroup : (model as string),
        filterIndex,
      })
    );
    dispatch(setPageNumber(1));

    GTM.setFilterInformation(GTMFilterCategory.MAKE_MODEL, {
      model: model as string,
    });
  };

  const onAddFilter = () => {
    dispatch(addFilter());
  };

  const onDeleteFilter = (filterIndex: number) => {
    dispatch(
      removeFilter({
        filterIndex,
      })
    );

    dispatch(setPageNumber(1));
  };

  const selectedMakes = useSelector(selectPCPFilterMakeModelSelectedMakes);

  const filteredMakeItems = makeItems.filter(
    ({ text }) => !selectedMakes.includes(text)
  );

  const isDisabled = (index: number) => {
    return index === 0 ? false : !selectedMakes[index - 1];
  };

  return (
    <>
      {Array.from(Array(makeModelFilterCount).keys()).map((index) => {
        const key = `make-model-trim-filter-item-${index}`;

        return (
          <div
            className={clsx(
              isDisabled(index) && 'pointer-events-none opacity-30',
              '[&:not(:first-child)]:mt-40px'
            )}
            key={key}
          >
            <div className="mb-20px">
              <MakeModelVariant
                id={key}
                index={index}
                makeItems={filteredMakeItems || []}
                modelLabel={modelLabel}
                makeLabel={makeLabel}
                onMakeChange={onMakeChange}
                onModelChange={onModelChange}
                selectedMake={filterData[index].makes.selected}
                selectedModel={
                  selectedModelGroup
                    ? selectedModelGroup
                    : filterData[index].models.selected
                }
                isPcpAndHomepageModelDropdown={true}
                modelDropdownItemsPcpAndHomepage={makeModels[index]}
              />
            </div>
            {makeModelFilterCount > 1 && (
              <div className="pb-40px">
                <TextLink
                  isDarkBg={true}
                  onClick={() => onDeleteFilter(index)}
                  text={removeBtnText}
                  icon={<Close16 className="text-white" />}
                />
              </div>
            )}
            {index < makeModelFilterCount && (
              <hr className="border-purple90 border-t-1px monochrome:border-white" />
            )}
          </div>
        );
      })}
      {makeModelFilterCount !== maxFilterLength && (
        <div
          className={`${makeModelFilterCount === 1 ? 'pt-20px' : 'pt-40px'}`}
        >
          <TextLink
            isDarkBg={true}
            onClick={onAddFilter}
            text={addBtnText}
            icon={<Add16 />}
          />
        </div>
      )}
    </>
  );
};
export default MakeModelFilter;
